import { createSelector, createEntityAdapter } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const propertiesAdapter = createEntityAdapter({});

const initialState = propertiesAdapter.getInitialState();

export const propertiesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProperties: builder.query({
      query: (page = 1) => `/properties?page=${page}`,
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
      // Request new data or refer to it in cache
      // Default is 60 sec, for development using 5 sec
      // keepUnusedDataFor: 5,
      transformResponse: (responseData) => {
        const loadedProperties = responseData.publishedProperties.map(
          (property) => ({
            ...property,
            id: property._id,
          })
        );
        return {
          properties: loadedProperties,
          hasMore: responseData.hasMore,
          // totalPages: responseData.totalPages,
        };
      },
      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "Property", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Property", id })),
          ];
        } else return [{ type: "Property", id: "LIST" }];
      },
    }),
    addNewProperty: builder.mutation({
      query: (formDataToSubmit) => ({
        url: "/properties",
        method: "POST",
        body: formDataToSubmit,
      }),
      invalidatesTags: [{ type: "Property", id: "LIST" }],
    }),
    updateProperty: builder.mutation({
      query: (formDataToSubmit) => ({
        url: "/properties",
        method: "PATCH",
        body: formDataToSubmit,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Property", id: arg.id },
      ],
    }),
    // Toggle publish/unpublish
    toggleActive: builder.mutation({
      query: ({ id, isActive }) => ({
        url: `/properties/${id}/toggle-active`,
        method: "PATCH",
        body: { isActive },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Property", id: arg.id },
      ],
    }),
    deleteProperty: builder.mutation({
      query: ({ id }) => ({
        url: `/properties`,
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Property", id: arg.id },
      ],
    }),
    getPropertyById: builder.query({
      query: (id) => `properties/${id}`,
      transformResponse: (responseData) => {
        responseData.id = responseData._id;
        delete responseData._id;
        return responseData;
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Property", id: arg.id },
      ],
    }),
    getMyPropertyById: builder.query({
      query: (id) => `properties/${id}/owner`,
      transformResponse: (responseData) => {
        responseData.id = responseData._id;
        delete responseData._id;
        return responseData;
      },
      // invalidatesTags: (result, error, arg) => [
      //   { type: "Property", id: arg.id },
      // ],
      providesTags: (result, error, id) => [{ type: "Property", id }],
    }),
    getMyProperties: builder.query({
      // query: () => ({
      //   url: `/properties/user`,
      //   method: "POST",
      //   // body: {},
      // }),
      // transformResponse: (responseData) => {
      //   const loadedProperties = responseData.map((property) => {
      //     property.id = property._id;
      //     return property;
      //   });
      //   return propertiesAdapter.setAll(initialState, loadedProperties);
      // },

      query: ({ page, status }) => ({
        url: `/properties/owner`, //?page=${page}`,
        method: "POST",
        body: {
          page,
          status: status !== "all" ? status : undefined, // Only send status if not "all"
        },
      }),
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
      // Request new data or refer to it in cache
      // Default is 60 sec, for development using 5 sec
      // keepUnusedDataFor: 5,
      transformResponse: (responseData) => {
        const loadedProperties = responseData.ownerProperties.map(
          (property) => ({
            ...property,
            id: property._id,
          })
        );
        return {
          properties: loadedProperties,
          hasMore: responseData.hasMore,
          // totalPages: responseData.totalPages,
        };
      },
      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "Property", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Property", id })),
          ];
        } else return [{ type: "Property", id: "LIST" }];
      },
    }),

    getMyMultiUnitProperties: builder.query({
      query: () => `properties/owner/multi-unit`,
      transformResponse: (responseData) => {
        const multiUnitProperties = responseData.map((property) => {
          const { _id, ...rest } = property; // Destructure to remove `_id`
          return { id: _id, ...rest }; // Map `_id` to `id` and retain the rest of the fields
        });
        return propertiesAdapter.setAll(initialState, multiUnitProperties);
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Property", id: arg.id },
      ],
    }),

    // Fetch Owner Nfts linked to properties
    getOwnerNftsLinkedProperty: builder.query({
      query: () => "/properties/owner/linked-nfts",
      validateStatus: (response, result) => {
        return response.status === 200 && !result.isError;
      },
      transformResponse: (responseData) => {
        const loadedNfts = responseData.map((nft) => {
          const { _id, ...rest } = nft; // Destructure to remove `_id`
          return { id: _id, ...rest }; // Map `_id` to `id` and retain the rest of the fields
        });
        return propertiesAdapter.setAll(initialState, loadedNfts);
      },
      providesTags: (result, error, arg) => {
        if (result?.ids) {
          return [
            { type: "Property", id: "LIST" },
            ...result.ids.map((id) => ({ type: "Property", id })),
          ];
        } else return [{ type: "Property", id: "LIST" }];
      },
    }),
  }),
});

export const {
  useGetPropertiesQuery,
  useAddNewPropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useGetPropertyByIdQuery,
  useGetMyPropertyByIdQuery,
  useGetMyPropertiesQuery,
  useGetMyMultiUnitPropertiesQuery,
  useToggleActiveMutation,
  useGetOwnerNftsLinkedPropertyQuery,
} = propertiesApiSlice;

// returns the query result object
export const selectPropertiesResult =
  propertiesApiSlice.endpoints.getProperties.select();

// creates memoized selector
const selectPropertiesData = createSelector(
  selectPropertiesResult,
  (propertiesResult) => propertiesResult.data // normalized state object with ids & entities
);

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllProperties,
  selectById: selectPropertyById,
  selectIds: selectPropertyIds,
  // Pass in a selector that returns the properties slice of state
} = propertiesAdapter.getSelectors(
  (state) => selectPropertiesData(state) ?? initialState
);
